import { useState } from 'react';
import type { FormEvent } from 'react';
import { api, type Server, type TerminalLocation } from '../../api';
import { useResource } from '../../hooks';
import { Terminal } from '../Terminal';
import type { TerminalShortcut } from '../Terminal';
import { ActionMenu, Field, Input, Section, Textarea } from '../primitives';
import { ConfirmDialog, ErrorNotice } from '../ui';
import { QuietEmpty } from '../project/ui';

/**
 * ServerTerminalSection is Server Detail's browser terminal (CO-190): a shell
 * on the server with an SSH key an owner saves once. The key is verified
 * against the server when it is saved, stored encrypted and never shown
 * again; the host key the server presents is pinned. Opening a shell needs
 * the terminal permission and is recorded; saving or removing the key is the
 * owner's alone.
 */
export function ServerTerminalSection({ server, canOpen, isOwner }: { server: Server; canOpen: boolean; isOwner: boolean }) {
  const [reload, setReload] = useState(0);
  const access = useResource(() => api.getServerTerminalAccess(server.id), [server.id, reload], undefined, { key: `server:${server.id}:terminal-access` });
  const [open, setOpen] = useState(false);
  const locations = useResource(() => (open ? api.getServerTerminalLocations(server.id) : Promise.resolve({ locations: [] as TerminalLocation[] })), [server.id, open], undefined, {
    key: `server:${server.id}:terminal-locations`,
  });
  const [editing, setEditing] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [host, setHost] = useState(server.hostname ?? '');
  const [port, setPort] = useState('22');
  const [username, setUsername] = useState('root');
  const [privateKey, setPrivateKey] = useState('');
  const [busy, setBusy] = useState<'save' | 'remove' | null>(null);
  const [error, setError] = useState<Error | null>(null);

  // A server reached over SSH already has its credential; it is managed with the server.
  const managedWithServer = server.access_kind === 'ssh';
  const current = access.data?.configured ? access.data.access : undefined;
  const showForm = !managedWithServer && isOwner && (editing || (access.data && !access.data.configured));

  async function save(event: FormEvent) {
    event.preventDefault();
    setBusy('save');
    setError(null);
    try {
      await api.configureServerTerminalAccess(server.id, { host: host.trim(), port: Number(port) || 22, username: username.trim(), private_key: privateKey });
      setPrivateKey('');
      setEditing(false);
      setReload((n) => n + 1);
    } catch (err) {
      setError(err as Error);
    } finally {
      setBusy(null);
    }
  }

  async function remove() {
    setBusy('remove');
    setError(null);
    try {
      await api.deleteServerTerminalAccess(server.id);
      setOpen(false);
      setReload((n) => n + 1);
    } catch (err) {
      setError(err as Error);
    } finally {
      setBusy(null);
      setConfirmRemove(false);
    }
  }

  return (
    <Section
      title="Terminal"
      id="terminal"
      testId="server-terminal"
      actions={
        current && canOpen ? (
          <>
            <button type="button" className="button button--secondary button--small" onClick={() => setOpen((value) => !value)} aria-expanded={open} data-testid="terminal-open">
              {open ? 'Close terminal' : 'Open terminal'}
            </button>
            {isOwner && !managedWithServer ? (
              <ActionMenu
                label="More terminal actions"
                size="sm"
                items={[
                  { label: 'Replace key', onClick: () => setEditing(true), testId: 'terminal-replace' },
                  { label: 'Remove key', danger: true, onClick: () => setConfirmRemove(true), testId: 'terminal-remove' },
                ]}
              />
            ) : null}
          </>
        ) : undefined
      }
    >
      {access.error && !access.data ? <ErrorNotice error={access.error} /> : null}
      {error ? <ErrorNotice error={error} /> : null}
      {!access.data && access.loading ? <QuietEmpty>Reading…</QuietEmpty> : null}

      {current ? (
        <p className="summary-line terminal-target" data-testid="terminal-target">
          <code translate="no">
            {current.username}@{current.host}:{current.port}
          </code>
          {current.host_key_fingerprint ? (
            <span className="muted">
              Host key <code translate="no">{current.host_key_fingerprint}</code>
            </span>
          ) : null}
        </p>
      ) : null}

      {access.data && !access.data.configured && !showForm ? <QuietEmpty>Needs an owner to save an SSH key</QuietEmpty> : null}

      {showForm ? (
        <form className="stack" onSubmit={save} data-testid="terminal-key-form">
          <div className="form-grid form-grid--2">
            <Field label="Host">
              <Input value={host} onChange={(e) => setHost(e.target.value)} required autoComplete="off" spellCheck={false} placeholder="203.0.113.7" />
            </Field>
            <Field label="Port">
              <Input type="number" min={1} max={65535} value={port} onChange={(e) => setPort(e.target.value)} />
            </Field>
            <Field label="User">
              <Input value={username} onChange={(e) => setUsername(e.target.value)} required autoComplete="off" spellCheck={false} />
            </Field>
          </div>
          <Field label="Private key" hint="PEM, stored encrypted and never shown again.">
            <Textarea
              value={privateKey}
              onChange={(e) => setPrivateKey(e.target.value)}
              required
              rows={6}
              autoComplete="off"
              spellCheck={false}
              placeholder="-----BEGIN OPENSSH PRIVATE KEY-----"
              className="mono"
            />
          </Field>
          <div className="row-actions">
            <button type="submit" className="button button--primary" disabled={busy !== null}>
              {busy === 'save' ? 'Connecting' : 'Save key'}
            </button>
            {editing ? (
              <button type="button" className="button button--secondary" onClick={() => setEditing(false)} disabled={busy !== null}>
                Cancel
              </button>
            ) : null}
          </div>
        </form>
      ) : null}

      {open && current && canOpen ? <Terminal serverId={server.id} environmentName={server.name} shortcuts={shortcutsOf(locations.data?.locations ?? [])} /> : null}

      {confirmRemove ? (
        <ConfirmDialog
          open={true}
          title="Remove the terminal key?"
          description="Forgets the key. The server is unchanged."
          confirmLabel="Remove key"
          danger={true}
          busy={busy === 'remove'}
          onConfirm={remove}
          onCancel={() => setConfirmRemove(false)}
        />
      ) : null}
    </Section>
  );
}

/** A line that enters a directory and shows it, safe for any path. */
export function goTo(path: string): string {
  return `cd '${path.replace(/'/g, `'\\''`)}' && pwd && ls`;
}

/** One shortcut per component: where it lives, labelled with what it is. Several projects or environments name theirs. */
function shortcutsOf(locations: TerminalLocation[]): TerminalShortcut[] {
  const projects = new Set(locations.map((l) => l.project_name));
  const environments = new Set(locations.map((l) => l.environment_name));
  return locations.map((l) => ({
    key: l.path,
    label: [projects.size > 1 ? l.project_name : null, l.component_name, environments.size > 1 ? l.environment_name : null].filter(Boolean).join(' · '),
    command: goTo(l.path),
  }));
}
